import React, { useRef, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { VolumeBucket, LEVEL_KEYS, LEVEL_COLORS } from '../types';

interface LogVolumeChartProps {
  volumeData: VolumeBucket[];
  chartTimeRange: { start: number; end: number } | null;
  chartZoomRange: { from: number; to: number } | null;
  onZoom: (from: number, to: number) => void;
  onClearZoom: () => void;
}

export const LogVolumeChart: React.FC<LogVolumeChartProps> = React.memo(({
  volumeData,
  chartTimeRange,
  chartZoomRange,
  onZoom,
  onClearZoom,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const dragOverlayRef = useRef<HTMLDivElement>(null);
  const zoomHighlightRef = useRef<HTMLDivElement>(null);
  const chartDragging = useRef(false);
  const dragStartPxRef = useRef(0);

  const showDragOverlay = (left: number, width: number) => {
    const el = dragOverlayRef.current;
    if (!el) return;
    el.style.display = 'block';
    el.style.left = `${left}px`;
    el.style.width = `${width}px`;
  };

  const hideDragOverlay = () => {
    const el = dragOverlayRef.current;
    if (el) el.style.display = 'none';
  };

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const rect = chartContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    chartDragging.current = true;
    dragStartPxRef.current = e.clientX - rect.left;
    showDragOverlay(e.clientX - rect.left, 0);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!chartDragging.current) return;
    e.preventDefault();
    const rect = chartContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const left = Math.min(dragStartPxRef.current, x);
    const width = Math.abs(x - dragStartPxRef.current);
    showDragOverlay(left, width);
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    hideDragOverlay();
    if (!chartDragging.current) return;
    chartDragging.current = false;

    const rect = chartContainerRef.current?.getBoundingClientRect();
    if (!rect || volumeData.length === 0 || !chartTimeRange) return;

    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const leftPx = Math.min(dragStartPxRef.current, x);
    const rightPx = Math.max(dragStartPxRef.current, x);

    if (rightPx - leftPx < 5) return;

    const leftFrac = Math.max(0, leftPx / rect.width);
    const rightFrac = Math.min(1, rightPx / rect.width);
    const { start, end } = chartTimeRange;
    const rangeMs = end - start;
    if (rangeMs <= 0) return;

    const fromMs = start + leftFrac * rangeMs;
    const toMs = start + rightFrac * rangeMs;

    if (toMs - fromMs > 1000) {
      onZoom(fromMs, toMs);

      const el = zoomHighlightRef.current;
      if (el) {
        el.style.display = 'block';
        el.style.left = `${leftFrac * 100}%`;
        el.style.width = `${(rightFrac - leftFrac) * 100}%`;
      }
    }
  }, [volumeData.length, chartTimeRange, onZoom]);

  const zoomDimOverlays = chartZoomRange && chartTimeRange ? (() => {
    const { start, end } = chartTimeRange;
    const rangeMs = end - start;
    if (rangeMs <= 0) return null;
    const leftPct = `${Math.max(0, (chartZoomRange.from - start) / rangeMs) * 100}%`;
    const rightPct = `${Math.max(0, 1 - (chartZoomRange.to - start) / rangeMs) * 100}%`;
    return (
      <>
        <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: leftPct, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none', zIndex: 2 }} />
        <div style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: rightPct, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none', zIndex: 2 }} />
      </>
    );
  })() : null;

  return (
    <Box
      sx={{
        px: 2, pt: 1.5, pb: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Log volume
        </Typography>
        {chartZoomRange && (
          <>
            <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 600, fontSize: '0.68rem' }}>
              (zoomed: {new Date(chartZoomRange.from).toLocaleTimeString()} – {new Date(chartZoomRange.to).toLocaleTimeString()})
            </Typography>
            <Button
              size="small"
              variant="text"
              color="secondary"
              onClick={onClearZoom}
              sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0, px: 0.5, minWidth: 'auto', lineHeight: 1.4, fontWeight: 600 }}
            >
              Reset zoom
            </Button>
          </>
        )}
        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', fontSize: '0.65rem', userSelect: 'none' }}>
          Drag to zoom
        </Typography>
      </Box>

      <Box
        ref={chartContainerRef}
        sx={{
          position: 'relative', minHeight: 100,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.04)',
          borderRadius: 1, mx: -0.5, px: 0.5, mb: 0.5,
        }}
      >
        <Box sx={{ pointerEvents: 'none', '& .recharts-wrapper, & .recharts-wrapper svg': { outline: 'none' } }}>
          <ResponsiveContainer width="100%" height={100}>
            <BarChart data={volumeData} barCategoryGap={1}>
              <XAxis dataKey="timeLabel" tick={{ fontSize: 10 }} interval="preserveStartEnd" tickLine={false} axisLine={false} />
              <YAxis hide />
              {LEVEL_KEYS.map((level) => (
                <Bar key={level} dataKey={level} stackId="volume" fill={LEVEL_COLORS[level]} isAnimationActive={false} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>

        <div
          ref={zoomHighlightRef}
          style={{
            display: chartZoomRange ? 'block' : 'none',
            position: 'absolute', top: 0, height: '100%',
            backgroundColor: 'rgba(100, 149, 237, 0.15)',
            borderLeft: '2px solid rgba(100, 149, 237, 0.7)',
            borderRight: '2px solid rgba(100, 149, 237, 0.7)',
            pointerEvents: 'none', zIndex: 3,
            ...(chartZoomRange && chartTimeRange ? (() => {
              const { start, end } = chartTimeRange;
              const rangeMs = end - start;
              if (rangeMs <= 0) return {};
              return {
                left: `${Math.max(0, (chartZoomRange.from - start) / rangeMs) * 100}%`,
                width: `${(Math.min(1, (chartZoomRange.to - start) / rangeMs) - Math.max(0, (chartZoomRange.from - start) / rangeMs)) * 100}%`,
              };
            })() : {}),
          }}
        />

        {zoomDimOverlays}

        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={{ position: 'absolute', inset: 0, zIndex: 5, cursor: 'crosshair', userSelect: 'none', touchAction: 'none' }}
        >
          <div
            ref={dragOverlayRef}
            style={{
              display: 'none', position: 'absolute', top: 0, height: '100%',
              backgroundColor: 'rgba(100, 149, 237, 0.25)',
              borderLeft: '2px solid rgba(100, 149, 237, 0.9)',
              borderRight: '2px solid rgba(100, 149, 237, 0.9)',
              pointerEvents: 'none',
            }}
          />
        </div>
      </Box>
    </Box>
  );
});

LogVolumeChart.displayName = 'LogVolumeChart';
